import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApiMutation } from '@lumi/app-kit';
import {
  accountApi,
  readAccountToken,
  storeAccountToken,
  clearAccountToken,
  onAccountSessionLost,
} from '@/utils/api';
import { decodeJwt } from '@/utils/jwt';
import { getPasskeyAssertion, ceremonyDismissed } from '@/utils/passkey';

export interface AccountUser {
  userId: string;
  // Null for a phone-only account (issue 698, ADR 0093): its token carries no
  // `email` claim. The "signed in as" line reads the identifier from
  // `GET /me/account` instead (AccountWorkspaces).
  email: string | null;
}

export const useAccountAuthStore = defineStore('accountAuth', () => {
  const token = ref(readAccountToken());
  // Identity comes out of the access token (which carries the address), so a
  // reload keeps the greeting without a second round trip.
  const user = ref<AccountUser | null>(null);

  const isAuthenticated = computed(() => !!token.value);

  function hydrateFromToken() {
    const payload = token.value ? decodeJwt(token.value) : null;
    user.value = payload?.userId
      ? { userId: payload.userId, email: typeof payload.email === 'string' && payload.email ? payload.email : null }
      : null;
  }

  function setToken(value: string) {
    token.value = value;
    storeAccountToken(value);
    hydrateFromToken();
  }

  function logout() {
    token.value = '';
    user.value = null;
    clearAccountToken();
  }

  /**
   * PERSON-FIRST AUTHENTICATION (D40). The body carries NO `tenantSlug` — not an
   * empty one, none — and that omission is the feature, not an oversight:
   *
   *   * user-api's login gates a workspace's suspension ladder AND its enforce-SSO
   *     policy on a submitted `tenantSlug`. Naming a workspace here would hand a
   *     suspension a veto over a legal obligation, which is the exact failure this
   *     host exists to prevent; and
   *   * a person may not even know which of their workspaces is locked, so there is
   *     no slug for them to name.
   *
   * The SSO policy is NOT lost by this: it is enforced per workspace, at the point
   * of action, by user-api's account-host resolver (`account-host.js`) — a
   * workspace that mandates SSO answers `sso_required` on every one of its rows and
   * routes here. Signing in is only identifying the PERSON; it grants nothing over
   * any workspace by itself.
   */
  // A sign-in is NEVER transport (issue 195, T4): somebody pressed a button and is
  // waiting for an answer, so it converts with a tier rather than taking a
  // file-level exemption — which on this host, where the login is the only write in
  // the file, would have swallowed every refused sign-in silently.
  //
  // TIER `inlineEdit`: what confirms a sign-in is the workspace list appearing in
  // place of the card. A toast fired at that same moment is a toast nobody reads.
  //
  // CHANNEL `inPlace`: the sign-in card renders the failure itself, right under the
  // fields it belongs to — which is what the `suppressGlobalError` this call used to
  // carry was saying, in the vocabulary that had no word for it. `AccountSignInCard`
  // reads `loginError`, and that render is the promise this channel makes.
  const {
    pending: signingIn,
    formError: loginError,
    reset: resetLoginError,
    mutate: signIn,
  } = useApiMutation<{ token: string }, [string, string]>({
    tier: 'inlineEdit',
    errorChannel: 'inPlace',
    run: async (email: string, password: string) => {
      const { data } = await accountApi.post('/auth/login', { email, password });
      // A system admin's password answers a second-factor challenge (issue 619, ADR
      // 0094), which only the console asks for; the card says so instead of
      // calling a correct password a failed sign-in.
      if (data?.mfaRequired === true) throw Object.assign(new Error('Second factor required'), { mfaRequired: true });
      // A 200 without a token is a failed sign-in: `run` has to REJECT for the seam
      // to treat it as one, or the card would be handed `ok: true` and no session.
      if (!data?.token) throw new Error('No token returned');
      // The refresh token is deliberately not kept: a short session on a host that
      // exists for a handful of one-off legal actions is the smaller footprint, and a
      // silent-refresh machinery here would be a second session model to keep honest.
      setToken(data.token);
      return data as { token: string };
    },
  });

  async function login(email: string, password: string) {
    return signIn(email, password);
  }

  /**
   * Signing in with a passkey (issue 698, ADR 0093) — the door a phone-only
   * account has here, since it has neither an address nor a password to type.
   * Usernameless, like the workspace login's: the browser offers what it holds for
   * the relying party and the assertion says whose it is.
   *
   * And, as with the password, NO `tenantSlug` — not an empty one, none (D40):
   * user-api's verify gates the workspace ladder and enforce-SSO on a submitted
   * slug, and this host names no workspace before the person has signed in.
   *
   * TIER `inlineEdit` and CHANNEL `inPlace`, for the reasons the password sign-in
   * gives above. A dismissed prompt resolves `null`, never an error: it is
   * something the person did on purpose.
   */
  const {
    pending: passkeySigningIn,
    formError: passkeyError,
    reset: resetPasskeyError,
    mutate: passkeySignIn,
  } = useApiMutation<{ token: string } | null, []>({
    tier: 'inlineEdit',
    errorChannel: 'inPlace',
    run: async () => {
      const { data: options } = await accountApi.post('/auth/passkey/options', {});
      let response;
      try {
        response = await getPasskeyAssertion(options);
      } catch (error) {
        if (ceremonyDismissed(error)) return null;
        throw error;
      }
      const { data } = await accountApi.post('/auth/passkey/verify', { response, challenge: options?.challenge });
      if (!data?.token) throw new Error('No token returned');
      setToken(data.token);
      return data as { token: string };
    },
  });

  async function loginWithPasskey() {
    return passkeySignIn();
  }

  // A 401 on any other route means the session died mid-visit; end it in the same
  // place an explicit sign-out does.
  onAccountSessionLost(() => {
    token.value = '';
    user.value = null;
  });

  hydrateFromToken();

  return {
    token,
    user,
    isAuthenticated,
    signingIn,
    loginError,
    resetLoginError,
    passkeySigningIn,
    passkeyError,
    resetPasskeyError,
    setToken,
    login,
    loginWithPasskey,
    logout,
  };
});
